import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CalendarCheck,
  FerrisWheel,
  Gift,
  HandCoins,
  Ticket,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.js';
import { SPIN_QUERY_KEYS, fetchSpinEvent, hasSpinWaiting } from '@/features/spin/api.js';

interface Shortcut {
  to: string;
  /** A bonus-hub tab to open (`to?tab=…`). */
  tab?: string;
  labelKey: string;
  icon: LucideIcon;
}

// One-tap reach on phones (< lg) for the money actions and every bonus-hub
// tab. Home is the current page and Wallet is the bottom-nav center CTA, so
// neither is repeated; account pages stay in the TopBar avatar menu.
const SHORTCUTS: Shortcut[] = [
  { to: '/wallet/deposit',  labelKey: 'common.deposit',                          icon: ArrowDownToLine },
  { to: '/wallet/withdraw', labelKey: 'common.withdraw',                         icon: ArrowUpFromLine },
  { to: '/promos', tab: 'promotions', labelKey: 'promo:hub.tabs.promotions',     icon: Gift            },
  { to: '/promos', tab: 'checkin',    labelKey: 'nav.checkin',                   icon: CalendarCheck   },
  { to: '/promos', tab: 'cashback',   labelKey: 'promo:hub.tabs.cashback',       icon: HandCoins       },
  { to: '/promos', tab: 'referral',   labelKey: 'nav.referral',                  icon: UsersRound      },
  { to: '/promos', tab: 'spin',       labelKey: 'promo:hub.tabs.spin',           icon: FerrisWheel     },
  { to: '/promos', tab: 'redeem',     labelKey: 'promo:hub.tabs.redeem',         icon: Ticket          },
];

/**
 * Mobile quick-access grid (< lg): 4-column raised tiles, each an icon well
 * over a label, under the lobby banner. The look lives in `.bf-tile` /
 * `.bf-tile-well` (globals.css), built entirely from mixes of the tenant's
 * `primaryColor` (`--primary`, set by `applyBranding`), so no colour here is
 * fixed per brand. The Wheel tile carries a dot while a Lucky Spin waits.
 */
export function MobileShortcuts() {
  const { t } = useTranslation('common');
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  // The guest lobby renders this grid too and must not fire the authed probe.
  const spinWaiting = hasSpinWaiting(
    useQuery({
      queryKey: SPIN_QUERY_KEYS.event,
      queryFn: fetchSpinEvent,
      enabled: isAuthed,
      staleTime: 15_000,
    }).data,
  );
  return (
    <nav
      aria-label={t('nav.menu')}
      className="lg:hidden -mx-5 px-3 grid grid-cols-4 auto-rows-fr gap-2.5"
    >
      {SHORTCUTS.map(({ to, tab, labelKey, icon: ItemIcon }) => (
        <Link
          key={`${to}:${tab ?? ''}`}
          to={to as '/'}
          search={tab ? { tab } : undefined}
          className="bf-tile relative flex flex-col items-center gap-2 overflow-hidden rounded-2xl px-1.5 pt-2.5 pb-2.5 text-center text-[var(--primary-on)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <span className="bf-tile-well relative grid aspect-square w-[62%] place-items-center">
            <ItemIcon className="size-6" aria-hidden />
          </span>
          <span className="relative flex w-full flex-1 items-center justify-center">
            <span className="line-clamp-2 text-[12.5px] font-bold leading-tight [text-shadow:0_1px_3px_rgba(0,0,0,0.45)]">
              {t(labelKey)}
            </span>
          </span>
          {tab === 'spin' && spinWaiting && (
            <>
              <span
                aria-hidden
                className="absolute right-2 top-2 size-2.5 rounded-full bg-[var(--warning)] ring-2 ring-[var(--primary)] motion-safe:animate-pulse"
              />
              {' '}
              <span className="sr-only">{t('promo:hub.spinWaiting')}</span>
            </>
          )}
        </Link>
      ))}
    </nav>
  );
}
